import { useState, useEffect } from 'react';
import FormGroup from '@mui/material/FormGroup';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FormHelpText from './form-helpText';

interface FormInputDateItem {
  title?: string;
  required?: boolean;
  maxToday?: boolean;
  validation?: string;
  validationMessage?: string;
  helpText?: string;
}

interface FormInputDateProps {
  item: FormInputDateItem;
  unique: string;
  submitted: boolean;
}

const FormInputDate = ({ item, unique, submitted }: FormInputDateProps) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);

  const todayMax = item.maxToday ? new Date().toISOString().split('T')[0] : undefined;

  const validate = (val: string) => {
    if (item.required && val === '') {
      setError(true);
    } else if (item.validation) {
      const regex = new RegExp(item.validation);
      if (!regex.test(val)) {
        setError(true);
      } else {
        setError(false);
      }
    } else {
      setError(false);
    }
  };

  useEffect(() => {
    if (submitted) {
      validate(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted]);

  return (
    <>
      <FormGroup className="py-4">
        <InputLabel className="font-normal text-[#42474a] pb-2 tra-text-web-body-m">{`${
          item.title
        }${item.required ? ' *' : ''}`}</InputLabel>
        <TextField
          type="date"
          value={value}
          variant="outlined"
          fullWidth
          onBlur={(e) => validate(e.target.value)}
          onChange={(e) => setValue(e.target.value)}
          id={`form-${unique}`}
          name={unique}
          inputProps={{
            title: `${item.title}${item.required ? ' *' : ''}`,
            ...(todayMax && { max: todayMax }),
          }}
          required={item.required}
          aria-required={item.required ? 'true' : 'false'}
          aria-describedby={`${unique}-help`}
          error={error}
          helperText={
            error
              ? item.validationMessage
                ? item.validationMessage
                : `${item.title} is required`
              : ''
          }
          FormHelperTextProps={{
            className: 'mt-1',
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
              boxShadow: error ? `0px 0px 0px 3px rgba(209, 26, 10, 0.4)` : '',
              '&.Mui-focused': {
                boxShadow: error
                  ? `0px 0px 0px 3px rgba(209, 26, 10, 0.4)`
                  : '0px 0px 0px 3px rgba(217, 94, 22, 0.4)',
              },
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ecedee',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {error ? (
                  <CancelIcon color="error" />
                ) : value === '' ? (
                  ''
                ) : (
                  <CheckCircleIcon color="primary" />
                )}
              </InputAdornment>
            ),
          }}
        />
        <FormHelpText
          display={!!item.helpText}
          unique={unique}
          content={item.helpText !== undefined ? item.helpText : ''}
        />
      </FormGroup>
    </>
  );
};

export default FormInputDate;
